import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import { format } from "sql-formatter";
import { xml2js, js2xml } from "xml-js";
import Papa from "papaparse";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Copy, RotateCcw } from "lucide-react";
import { copyToClipboard } from "@/lib/utils";

const SAMPLE_XML = `<company name="Creative Code Corp" industry="Software">
  <employees>
    <employee id="101" active="true">
      <name>John Connor</name>
      <role>Lead Systems Architect</role>
      <skills>
        <skill>TypeScript</skill>
        <skill>React</skill>
        <skill>Go</skill>
      </skills>
    </employee>
    <employee id="102" active="false">
      <name>Sarah Connor</name>
      <role>Cyber Security Specialist</role>
      <skills>
        <skill>Pentesting</skill>
        <skill>Kali Linux</skill>
      </skills>
    </employee>
  </employees>
</company>`;

export function XmlFormatter() {
  const [input, setInput] = useState(SAMPLE_XML);
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const process = () => {
    try {
      const obj = xml2js(input, { compact: false });
      setOutput(js2xml(obj, { compact: false, spaces: 2 }));
    } catch(e:any) {
      setOutput("Error: " + e.message);
    }
  };

  useEffect(() => {
    process();
  }, []);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="XML Formatter & Beautifier Online - Pretty Print XML"
      description="Format and pretty-print messy, minified XML documents with customizable indent levels instantly in your browser."
      h1="XML Formatter"
      category="JSON / Data"
      categoryPath="/csv-editor"
      howToUse={[
        "Paste your compact, cluttered or unindented XML into the text block.",
        "Click the format button to rebuild parent-child tag hierarchy structures instantly.",
        "Copy your clean XML output block."
      ]}
      faq={[
        { question: "Why is XML formatting useful?", answer: "It ensures closing tags are matched and aligns nodes, which drastically speeds up config parsing." },
        { question: "Is my XML content safe?", answer: "Yes, all processing is restricted to your local client browser session." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button onClick={process} className="bg-slate-900 hover:bg-slate-800 text-white">Format & Beautify XML</Button>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setInput(SAMPLE_XML); setTimeout(process, 50); }}
              className="text-xs gap-1"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset Sample
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output || output.startsWith("Error")}
              className="text-xs gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy XML"}
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Raw XML Input</label>
            <Textarea value={input} onChange={e => setInput(e.target.value)} placeholder="<root><item>Hello</item></root>" className="min-h-[220px] font-mono text-xs" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Beautified XML Output</label>
            <Textarea value={output} readOnly className="min-h-[220px] bg-slate-50 font-mono text-xs text-slate-700" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_SQL = `SELECT u.id AS user_id, u.username, p.title AS project_title, t.task_name, t.due_date, COUNT(c.id) AS comment_count FROM users u INNER JOIN projects p ON u.id = p.owner_id LEFT JOIN tasks t ON p.id = t.project_id LEFT JOIN comments c ON t.id = c.task_id WHERE u.status = 'active' AND t.priority IN ('high', 'critical') GROUP BY u.id, u.username, p.title, t.task_name, t.due_date HAVING COUNT(c.id) > 2 ORDER BY t.due_date ASC, comment_count DESC LIMIT 100 OFFSET 0;`;

export function SqlFormatterTool() {
  const [input, setInput] = useState(SAMPLE_SQL);
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const process = () => {
    try {
      setOutput(format(input));
    } catch(e:any) {
      setOutput("Error: " + e.message);
    }
  };

  useEffect(() => {
    process();
  }, []);

  const handleCopy = () => {
    copyToClipboard(output).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <ToolPageLayout
      title="SQL Formatter & Beautifier Online - Clean SQL Queries"
      description="Format and pretty-print your complex database SQL queries instantly. Supports multiple dialects including Postgres, MySQL, and MSSQL."
      h1="SQL Formatter"
      category="JSON / Data"
      categoryPath="/csv-editor"
      howToUse={[
        "Paste your messy, flat SQL query into the input container.",
        "Click the SQL Format button.",
        "Copy your beautifully indented query with uppercase commands."
      ]}
      faq={[
        { question: "What SQL syntaxes are supported?", answer: "This tool formats standard SQL queries for PostgreSQL, MySQL, SQL Server, and Oracle databases." },
        { question: "Are my schema names transmitted?", answer: "No, parsing operates offline in RAM, safeguarding database security." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button onClick={process} className="bg-slate-900 hover:bg-slate-800 text-white">Format & Beautify SQL</Button>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setInput(SAMPLE_SQL); setTimeout(process, 50); }}
              className="text-xs gap-1"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset Sample
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopy}
              disabled={!output || output.startsWith("Error")}
              className="text-xs gap-1"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Copied!" : "Copy SQL"}
            </Button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Raw/Messy SQL Query Input</label>
            <Textarea value={input} onChange={e => setInput(e.target.value)} placeholder="SELECT * FROM users WHERE id = 1" className="min-h-[220px] font-mono text-xs" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Beautified SQL Output</label>
            <Textarea value={output} readOnly className="min-h-[220px] bg-slate-50 font-mono text-xs text-slate-700" />
          </div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

const SAMPLE_CSV = `TransactionID,Date,Customer,Product,Category,Amount,PaymentMethod,Status
TXN-88019,2026-07-10,Amelia Earhart,Noise-Cancelling Headphones,Audio,199.99,Credit Card,Settled
TXN-88020,2026-07-11,Albert Einstein,Mechanical Keyboard,Accessories,89.50,PayPal,Pending
TXN-88021,2026-07-11,Marie Curie,Ergonomic Office Chair,Office,249.00,Credit Card,Settled
TXN-88022,2026-07-12,Nikola Tesla,USB-C Dual Docking Station,Connectivity,149.00,Apple Pay,Settled
TXN-88023,2026-07-13,Charles Darwin,UltraWide 4K IPS Monitor,Hardware,449.99,Bank Transfer,Refunded`;

export function CsvViewer() {
  const [input, setInput] = useState(SAMPLE_CSV);
  const [data, setData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);

  const process = () => {
    try {
      const parsed = Papa.parse(input, { header: true });
      setData(parsed.data);
      if (parsed.meta.fields) {
        setHeaders(parsed.meta.fields);
      } else if (parsed.data.length > 0) {
        setHeaders(Object.keys(parsed.data[0]));
      }
    } catch(e:any) {
      alert("Error: " + e.message);
    }
  };

  useEffect(() => {
    process();
  }, []);

  return (
    <ToolPageLayout
      title="CSV Viewer Online - Free Interactive Spreadsheet Grid Reader"
      description="View raw comma-separated CSV spreadsheets in an elegant interactive table grid format completely client-side. No uploads required."
      h1="CSV Viewer"
      category="JSON / Data"
      categoryPath="/csv-editor"
      howToUse={[
        "Paste your comma-separated spreadsheet values into the editor.",
        "Click 'Render Grid View'.",
        "See data cleanly structured in columns and rows immediately."
      ]}
      faq={[
        { question: "What is a CSV file?", answer: "CSV (Comma-Separated Values) files store tabular databases as plain text separated by commas." },
        { question: "Are my spreadsheets secure?", answer: "Yes. Processing takes place locally in browser RAM." }
      ]}
    >
      <div className="space-y-4">
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <label className="block text-xs font-semibold text-slate-500 uppercase">Comma-Separated Text Input</label>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => { setInput(SAMPLE_CSV); setTimeout(process, 50); }}
              className="text-xs py-1 h-7 gap-1"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Reset Sample
            </Button>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Textarea value={input} onChange={e => setInput(e.target.value)} placeholder="name,age\nJohn,30" className="min-h-[120px] w-full font-mono text-xs" />
            <Button onClick={process} className="shrink-0 h-12 sm:h-auto bg-slate-900 hover:bg-slate-800 text-white font-medium px-6">Render Grid View</Button>
          </div>
        </div>
        {data.length > 0 && (
          <div className="overflow-x-auto border border-slate-200 rounded-xl shadow-xs">
            <table className="w-full text-xs text-left border-collapse">
              <thead className="bg-slate-50 text-slate-700 font-bold border-b border-slate-200">
                <tr>
                  {headers.map(h => <th key={h} className="px-4 py-3 font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {data.map((row, i) => (
                  <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                    {headers.map(h => <td key={h} className="px-4 py-3 text-slate-700">{row[h]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}

export function DataTools2() {
  return (
    <ToolPageLayout
      title="XML, SQL Formatter & CSV Spreadsheet Viewer Online"
      description="Format XML documents, beautify messy SQL database queries, and parse CSV spreadsheet rows into an interactive grid view with our offline-ready developer utilities."
      h1="Database & Spreadsheet Utilities"
      category="JSON / Data"
      categoryPath="/csv-editor"
      howToUse={[
        "Select the desired database or document tab below (XML Formatter, SQL Formatter, or CSV Viewer).",
        "Paste your unformatted database queries, nesting tags, or raw table texts.",
        "Click the formatting button to instantly normalize code indentation or map columns.",
        "Use copy helpers to retrieve clean results instantly into your local development environments."
      ]}
      faq={[
        { question: "Is my database SQL query secure here?", answer: "Yes. Absolutely all formatting is calculated inside your browser using client-side JavaScript packages. No queries or structural information are sent to external networks." },
        { question: "What databases are supported by the SQL Formatter?", answer: "The linter/formatter parses generic standard ANSI SQL statements. It works beautifully for PostgreSQL, MySQL, SQLite, Oracle, and MS SQL dialects." }
      ]}
    >
      <div className="space-y-16">
        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs">
          <h2 className="text-xl font-bold text-slate-950 mb-1">
            1. XML Beautifier & Indenter
          </h2>
          <p className="text-xs text-slate-500 mb-6">Pretty-print complex multi-layered XML tag schemas, restoring clean vertical indent levels and closing elements.</p>
          <XmlFormatter />
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs">
          <h2 className="text-xl font-bold text-slate-950 mb-1">
            2. SQL Query Beautifier
          </h2>
          <p className="text-xs text-slate-500 mb-6">Convert compact or single-line database statements into cleanly indented structures with highlighted keywords.</p>
          <SqlFormatterTool />
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs">
          <h2 className="text-xl font-bold text-slate-950 mb-1">
            3. CSV Table grid Viewer
          </h2>
          <p className="text-xs text-slate-500 mb-6">Import raw comma-separated values and render them instantly inside a clean spreadsheet style grid layout.</p>
          <CsvViewer />
        </section>
      </div>
    </ToolPageLayout>
  );
}
